import { openDB } from 'idb';
import { Storage, DEFAULT_STORE, ALL_STORES, DEVICE_SETTINGS, BASE_CURRENCY, REGISTRATION_DETAILS, FLOAT } from './Storage';

const LEGACY_DB = 'keyval-store';
const LEGACY_STORE = 'keyval';
const LEGACY_KEY = 'vuex';

const NEW_DB = 'vuex-db';
const NEW_STORE = 'vuex-store';

/**
 * After copying legacy Vuex state into the new IndexedDB, also mirror critical values
 * into localStorage via Storage.js so the new app (which reads from localStorage) can
 * see them immediately during boot (before Vuex has rehydrated or reacted to the new DB).
 */
async function backfillLocalStorageFromLegacy(oldState) {
    try {
        if (!oldState || typeof oldState !== 'object') return;

        // defaultStore
        if (oldState.defaultStore && localStorage.getItem(DEFAULT_STORE) == null) {
            await Storage.setDefaultStore(oldState.defaultStore);
            console.log('[Migration] ➕ Backfilled defaultStore into localStorage');
        }
        // allStores
        if (oldState.allStores && localStorage.getItem(ALL_STORES) == null) {
            await Storage.setAllStores(oldState.allStores);
            console.log('[Migration] ➕ Backfilled allStores into localStorage');
        }
        // device settings
        if (oldState.device_settings && localStorage.getItem(DEVICE_SETTINGS) == null) {
            await Storage.setDeviceSettings(oldState.device_settings);
            console.log('[Migration] ➕ Backfilled device_settings into localStorage');
        }
        // base currency
        if (oldState.baseCurrency && localStorage.getItem(BASE_CURRENCY) == null) {
            await Storage.setBaseCurrency(oldState.baseCurrency);
            console.log('[Migration] ➕ Backfilled baseCurrency into localStorage');
        }
        // registration details
        if (oldState.registrationDetails && localStorage.getItem(REGISTRATION_DETAILS) == null) {
            await Storage.setRegistrationDetails(oldState.registrationDetails);
            console.log('[Migration] ➕ Backfilled registrationDetails into localStorage');
        }
        // float
        if (oldState.float && localStorage.getItem(FLOAT) == null) {
            await Storage.setFloat(oldState.float);
            console.log('[Migration] ➕ Backfilled float into localStorage');
        }
    } catch (e) {
        console.warn('[Migration] ⚠️ Backfill to localStorage encountered an issue:', e);
    }
}

export async function migrateLegacyVuexState() {
    console.log('[Migration] Checking for legacy Vuex state...');

    try {
        // Open legacy IndexedDB
        const legacyDb = await openDB(LEGACY_DB, 1);
        if (!legacyDb.objectStoreNames.contains(LEGACY_STORE)) {
            console.log('[Migration] Legacy store not found; skipping migration.');
            return;
        }
        const oldState = await legacyDb.get(LEGACY_STORE, LEGACY_KEY);
        console.log('[Migration] Fetched legacy state:', oldState);

        if (!oldState) {
            console.log('[Migration] No legacy Vuex state found to migrate.');
            return;
        }

        // Open or create new transactional IndexedDB
        const newDb = await openDB(NEW_DB, 1, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(NEW_STORE)) {
                    db.createObjectStore(NEW_STORE);
                }
            }
        });

        // Store legacy state in the new DB
        const tx = newDb.transaction(NEW_STORE, 'readwrite');
        await tx.store.put(oldState, LEGACY_KEY);
        await tx.done;
        console.log('✅ Migration wrote to vuex-db/vuex-store.');

        // Also backfill localStorage for keys the new app expects there
        await backfillLocalStorageFromLegacy(oldState);

        // Optional: delete old data
        await legacyDb.delete(LEGACY_STORE, LEGACY_KEY);
        console.log('🧹 Cleaned up old legacy Vuex state from keyval-store.');
    } catch (err) {
        console.error('❌ Vuex state migration failed:', err);
    }
}
